/**
 *
 * GuideMeB
 *
 */

import React, { useContext, useEffect, useReducer, useRef } from 'react';
import { useQuery } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Modal from '@material-ui/core/Modal';
import Paper from '@material-ui/core/Paper';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { setGuideMeOpened } from 'containers/HomePage/actions';
import { makeSelectGuideMeOpened } from 'containers/HomePage/selectors';
import {
  makeSelectClientDetails,
  makeSelectSiteCopy,
} from 'containers/Main/selectors';
import { getContenfulFilters } from 'containers/Main/utils';
import {
  getAlgoliaRatingQuery,
  getAlgoliaTopicsQuery,
} from 'containers/HomePage/services/api-helper';
import { getResourcesIndex } from 'containers/Resources/utils';
import SearchIcon from 'components/SearchBar/searchIcon.svg';
import AlgoliaClient from 'utils/algoliaService';
import contentful from 'utils/contentfulService';
import Mixpanel from 'utils/mixpanelService';
import GuideMeContext from './GuideMeContext';
import Content from './Content';
import Stepper from './Stepper';
import PageContext from '../../PageContext';

const useStyles = makeStyles(theme => ({
  paperRoot: {
    width: '70%',
    maxWidth: '70%',
    borderRadius: '5px !important',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      height: '100%',
      maxHeight: '100%',
      margin: 0,
    },
  },
  actions: {
    padding: '28px 24px 0 24px',
  },
  multipleActions: {
    padding: '28px 24px 0 24px',
    justifyContent: 'space-between',
  },
  header: {
    paddingTop: 32,
    '& h2': {
      fontFamily: 'PoppinsSemibold',
      fontSize: 32,
      textAlign: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '30px 30px 0 30px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '20px 20px 0 20px',
      '& h2': {
        fontFamily: 'PoppinsSemibold',
        fontSize: 25,
      },
    },
    [theme.breakpoints.down('414')]: {
      marginTop: 20,
    },
  },
  footer: {
    justifyContent: 'center',
    padding: '20px 24px',
  },
  multipleActionsFooter: {
    justifyContent: 'flex-end',
    padding: '20px 36px',
    marginTop: 8,
    backgroundColor: '#F8F9F9',
    borderRadius: '0 0 5px 5px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      justifyContent: 'center',
    },
  },
  nextButton: {
    backgroundColor: props => `#${props.btnBgColor}`,
    color: props => `#${props.btnTextColor}`,
    borderRadius: 5,
    height: 52,
    ...theme.typography.subtitle1,
    fontSize: '1rem',
    textTransform: 'uppercase',
    paddingLeft: 32,
    paddingRight: 32,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginBottom: 8,
    },
  },
  skipButton: {
    ...theme.typography.body2,
    fontSize: '1rem',
    color: '#01619B',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  filterContainer: {
    position: 'relative',
    borderWidth: 1,
    borderRadius: 3,
    borderStyle: 'solid',
    borderColor: '#E3E3E3',
    height: 50,
    margin: '12px 24px 16px 24px',
  },
  filterIcon: {
    height: 24,
    position: 'absolute',
    left: 16,
    top: 13,
  },
  filter: {
    paddingLeft: 52,
    height: 50,
    width: '100%',
    cursor: 'text',
    border: 'none',
    borderRadius: 3,
    '-webkit-appearance': 'none',
    fontFamily: 'MadaRegular',
    fontSize: '1rem',
    color: '#4B4B4B',
    backgroundColor: 'transparent',
  },
  disabledButton: {
    opacity: '40%',
  },
}));

const queryFn = async (_key, clientDetails) => {
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );

  const contentfulFilters = getContenfulFilters(
    clientExcludedTopic,
    null,
    null,
    null,
    false,
  );

  const response = await contentful.getEntries({
    content_type: 'theme',
    'fields.reviewStatus': 'Accepted',
    ...contentfulFilters,
    include: 2,
  });

  const themeOptions = response.items.map(theme => ({
    fields: {
      ...theme.fields,
      topics: theme.fields.topics
        .filter(
          topic => topic.fields && topic.fields.reviewStatus === 'Accepted',
        )
        .map(topic => ({
          id: topic.sys.id,
          title: topic.fields.title,
          slug: topic.fields.slug,
          image: topic.fields.image || null,
          userRating: topic.fields.userRating || 0,
        })),
    },
    sys: {
      ...theme.sys,
    },
  }));

  return themeOptions;
};

const queryTypesFn = async (_key, clientDetails, topics) => {
  const resourcesWeightings = _get(
    clientDetails,
    'metadata.algoliaResourcesWeight',
  );

  const ratingFilter = getAlgoliaRatingQuery(clientDetails);
  const topicFilter = getAlgoliaTopicsQuery('title', true, clientDetails);

  let filters = '';
  _forEach(topics, (value, index) => {
    filters += `allTopics.title:'${value.title}'`;
    if (index !== topics.length - 1) {
      filters += ' OR ';
    }
  });
  if (ratingFilter) filters += ` AND ${ratingFilter}`;
  if (topicFilter) filters += ` AND ${topicFilter}`;

  const algoliaIndex = AlgoliaClient.initIndex(
    getResourcesIndex(resourcesWeightings),
  );

  const response = await algoliaIndex.searchForFacetValues('type', '', {
    attributesToHighlight: [],
    filters,
  });

  const ignoredTypes = ['Assessments', 'People', 'Topics'];
  const mediaTypeOptions = response.facetHits.filter(
    facet => !ignoredTypes.includes(facet.value),
  );

  return mediaTypeOptions;
};

const INITIAL_STATE = {
  filter: '',
  themes: [],
  topics: [],
  mediaTypes: [],
  currentStep: 0,
};

const ON_SELECT = 'on-select';
const ON_CHANGE = 'on-change';
const ON_NEXT = 'on-next-step';
const ON_PREVIOUS = 'on-previous-step';
const RESET = 'reset';

const reducer = (state, action) => {
  switch (action.type) {
    case ON_SELECT:
      return {
        ...state,
        themes: action.payload.themes || state.themes,
        topics: action.payload.topics || state.topics,
        mediaTypes: action.payload.mediaTypes || state.mediaTypes,
        currentStep: action.payload.topics
          ? state.currentStep + 1
          : state.currentStep,
      };
    case ON_CHANGE:
      return {
        ...state,
        filter: action.payload,
      };
    case ON_NEXT:
      return {
        ...state,
        currentStep: state.currentStep + 1,
        filter: '',
      };
    case ON_PREVIOUS:
      return {
        ...state,
        currentStep: state.currentStep - 1,
        themes: state.currentStep === 1 ? [] : state.themes,
        topics:
          state.currentStep === 1 || state.currentStep === 2
            ? []
            : state.topics,
        mediaTypes: state.currentStep === 2 ? [] : state.mediaTypes,
        filter: '',
      };
    case RESET:
      return INITIAL_STATE;
    default:
      throw new Error();
  }
};

const GuideMeB = () => {
  const { clientInfo } = useContext(PageContext);
  const { pageText } = clientInfo;
  const btnTextColor = _get(pageText, 'button.textColor', '13619B');
  const btnBgColor = _get(pageText, 'button.backgroundColor', 'E8F1F5');
  const classes = useStyles({ btnTextColor, btnBgColor });
  const dispatch = useDispatch();
  const history = useHistory();
  const open = useSelector(makeSelectGuideMeOpened());
  const dialogContentRef = useRef();
  const [state, localDispatch] = useReducer(reducer, INITIAL_STATE);
  const { filter, themes, topics, mediaTypes, currentStep } = state;

  const clientDetails = useSelector(makeSelectClientDetails());
  const siteCopy = useSelector(makeSelectSiteCopy());
  const guideMeSiteCopy = siteCopy.filter(el => el.slug === 'guide-me')[0];
  const guideMeConfig = {
    nextButtonLabel:
      _get(guideMeSiteCopy, 'pageCopy.nextButtonLabel') || 'Next',
    finishButtonLabel:
      _get(guideMeSiteCopy, 'pageCopy.simpleFinishButtonLabel') ||
      'Go to Topic Center',
    steps: [
      {
        type: 'themes',
        title:
          _get(guideMeSiteCopy, 'pageCopy.themes') ||
          "Let's start! Select where you want to focus.",
      },
      {
        type: 'topics',
        title:
          _get(guideMeSiteCopy, 'pageCopy.topics') ||
          'Great! Now tell us your topics of interest.',
      },
      {
        type: 'mediaTypes',
        title:
          _get(guideMeSiteCopy, 'pageCopy.resources') ||
          'Almost there! In what form do you want your resources today?',
      },
    ],
  };
  const themesQuery = useQuery(
    clientDetails && ['guide-me-themes', clientDetails],
    queryFn,
  );
  const typesQuery = useQuery(
    clientDetails &&
      !_isEmpty(topics) && ['guide-me-types', clientDetails, topics],
    queryTypesFn,
  );
  const { data: themesData } = themesQuery;
  const { data: typesData } = typesQuery;

  useEffect(() => {
    if (open) Mixpanel.track('Guide Me - Opened');
  }, [open]);

  if (_isEmpty(themesData)) return null;

  const onClose = () => {
    Mixpanel.track('Guide Me - Close Clicked');
    dispatch(setGuideMeOpened(false));
    localDispatch({ type: RESET });
  };
  const onSelect = (type, value) => {
    let added = true;
    let collectionData;
    if (type === 'themes') {
      if (themes.includes(value)) {
        added = false;
        collectionData = {
          themes: themes.filter(element => element !== value),
        };
      } else
        collectionData = {
          themes: themes.concat([value]),
        };
    } else if (type === 'topics') {
      if (topics.includes(value)) {
        added = false;
        collectionData = {
          topics: [],
        };
      } else
        collectionData = {
          topics: [value],
        };
    } else if (type === 'mediaTypes') {
      if (value === 'all') {
        if (mediaTypes.length === typesData.length) {
          added = false;
          collectionData = {
            mediaTypes: [],
          };
        } else
          collectionData = {
            mediaTypes: typesData,
          };
      } else if (mediaTypes.includes(value)) {
        added = false;
        collectionData = {
          mediaTypes: mediaTypes.filter(element => element !== value),
        };
      } else
        collectionData = {
          mediaTypes: mediaTypes.concat([value]),
        };
    }

    Mixpanel.track('Guide Me - Selection Changed', {
      type,
      value:
        _get(value, 'fields.title') ||
        _get(value, 'title') ||
        _get(value, 'value'),
      added,
    });

    localDispatch({ type: ON_SELECT, payload: collectionData });
  };

  const onPreviousStep = () => {
    Mixpanel.track('Guide Me - Previous Clicked', {
      step: currentStep,
    });
    localDispatch({ type: ON_PREVIOUS });
  };
  const onNextStep = () => {
    Mixpanel.track('Guide Me - Next Clicked', {
      step: currentStep,
    });
    localDispatch({ type: ON_NEXT });
  };
  const onContinue = useMediaTypes => {
    Mixpanel.track('Guide Me - To Topic Center Clicked', {
      skipped: !useMediaTypes,
      themes: themes.map(theme => `[${theme.fields.title}]`).join(','),
      topic: topics[0].slug,
      mediaTypes: !useMediaTypes
        ? undefined
        : mediaTypes.map(type => `[${type.value}]`).join(','),
    });
    localDispatch({ type: ON_NEXT });
    let url = `topics/${topics[0].slug}`;
    if (useMediaTypes && !_isEmpty(mediaTypes)) {
      url = url.concat(
        `?${mediaTypes.map(type => `types=${type.value}`).join('&')}`,
      );
    }

    dispatch(setGuideMeOpened(false));
    localDispatch({ type: RESET });

    history.push(url);
  };
  const onFilterChange = e =>
    localDispatch({ type: ON_CHANGE, payload: e.target.value });

  let collection;
  let optionsLength;
  const { type } = guideMeConfig.steps[currentStep];
  if (type === 'themes') {
    collection = themes;
    optionsLength = themesData.length;
  }
  if (type === 'topics') {
    collection = topics;

    const options = [];
    themes.forEach(theme => {
      options.push(...theme.fields.topics);
    });
    optionsLength = options.length;
  }
  if (type === 'mediaTypes') {
    collection = mediaTypes;
    optionsLength = (typesData || []).length;
  }

  return (
    <Modal open={open} onClose={onClose} disableBackdropClick hideBackdrop>
      <Paper
        elevation={24}
        className={classes.paperRoot}
        aria-labelledby="Guide me"
        aria-describedby="Find the most relevant resources"
      >
        <GuideMeContext.Provider
          value={{
            themes,
            topics,
            mediaTypes,
            currentStep,
            filter,

            themeOptions: themesData,
            mediaTypeOptions: typesData,
            config: guideMeConfig,

            onSelect,

            dialogContentRef,
          }}
        >
          <DialogActions
            disableSpacing
            classes={{
              root: currentStep > 0 ? classes.multipleActions : classes.actions,
            }}
          >
            {currentStep > 0 && (
              <ButtonBase onClick={onPreviousStep} className={classes.closeBtn}>
                <ChevronLeftIcon style={{ color: '#000000', fontSize: 24 }} />
              </ButtonBase>
            )}
            <ButtonBase onClick={onClose} className={classes.closeBtn}>
              <CloseIcon style={{ color: '#000000', fontSize: 24 }} />
            </ButtonBase>
          </DialogActions>
          <Stepper />
          <DialogTitle
            id="alert-dialog-title"
            classes={{ root: classes.header }}
          >
            {guideMeConfig.steps[currentStep].title}
          </DialogTitle>
          {guideMeConfig.steps[currentStep].useFilter && optionsLength > 5 && (
            <div className={classes.filterContainer}>
              <img
                className={classes.filterIcon}
                src={SearchIcon}
                alt="Filter"
              />
              <input
                onChange={onFilterChange}
                placeholder={guideMeConfig.steps[currentStep].filterPlaceholder}
                className={classes.filter}
                autoComplete="off"
                value={filter}
              />
            </div>
          )}
          <DialogContent ref={dialogContentRef}>
            <Content />
          </DialogContent>
          {currentStep !== 1 && (
            <DialogActions
              disableSpacing
              classes={{
                root:
                  currentStep > 1
                    ? classes.multipleActionsFooter
                    : classes.footer,
              }}
            >
              <ButtonBase
                onClick={
                  currentStep === guideMeConfig.steps.length - 1
                    ? () => onContinue(true)
                    : onNextStep
                }
                classes={{
                  root: classes.nextButton,
                  disabled: classes.disabledButton,
                }}
                style={{ minWidth: currentStep > 1 ? 179 : 246 }}
                disabled={collection.length === 0}
              >
                {currentStep === guideMeConfig.steps.length - 1
                  ? guideMeConfig.finishButtonLabel
                  : guideMeConfig.nextButtonLabel}
              </ButtonBase>
            </DialogActions>
          )}
        </GuideMeContext.Provider>
      </Paper>
    </Modal>
  );
};

export default GuideMeB;
